/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { DOMConversionMap, DOMConversionOutput, DOMExportOutput, EditorConfig, LexicalEditor, LexicalNode, NodeKey, SerializedEditor, SerializedLexicalNode, Spread } from "lexical";

import { $applyNodeReplacement, createEditor, DecoratorNode } from "lexical";
import * as React from "react";
import { Suspense } from "react";
import store from "redux/store";
import { getCDNSrc } from "utils/image/cdn";

const ImageComponent = React.lazy(() => import("./ImageComponent"));

export interface ClipData {
  src: string;
  documentKey: string;
  clipKey: string;
  type: string;
}

export interface ImagePayload {
  src: string;
  originalImageSrc: string;
  isUploaded: boolean;
  markupImageSrc?: string;
  sourceType: string;
  fullSizeImageSrcForCropping?: string;
  imageCropArea?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  timestamp?: number | string;
  key?: NodeKey;
  clipKey?: string;
  maxWidth?: number;
  height?: number | "inherit";
  width?: number | "inherit";
  documentKey?: string;
  videoInfo?: {
    videoType: string;
    videoUnique: string;
    videoOriginUrl: string;
    websiteOriginUrl: string;
    videoKey: string;
  };
  isLoading?: boolean;
  rawSrc?: string;
  setClipData?: () => Promise<ClipData | undefined>;
}

function isGoogleDocCheckboxImg(img: HTMLImageElement): boolean {
  return img.parentElement != null && img.parentElement.tagName === "LI" && img.previousSibling === null && img.getAttribute("aria-roledescription") === "checkbox";
}

function $convertImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  if (img.src.startsWith("file:///") || isGoogleDocCheckboxImg(img)) {
    return null;
  }
  const { src } = img;
  const node = $createImageNode({
    src,
    maxWidth: 1000,
    clipKey: "",
    documentKey: "",
    sourceType: "image",
    originalImageSrc: src,
    isUploaded: true,
    key: "",
  });
  return { node };
}

export type SerializedImageNode = Spread<ImagePayload, SerializedLexicalNode>;

const editorImageBlockErrorPlaceholderEn = getCDNSrc("https://slid-public-assets.s3.us-west-1.amazonaws.com/images/editor_image_block_error_placeholder_en.png") as string;
const editorImageBlockErrorPlaceholderKo = getCDNSrc("https://slid-public-assets.s3.us-west-1.amazonaws.com/images/editor_image_block_error_placeholder_ko.png") as string;

export class SlidImageNode extends DecoratorNode<JSX.Element> {
  private __data: ImagePayload;

  constructor(data: ImagePayload, key?: NodeKey) {
    super(key);
    this.__data = data;
  }

  isInline(): boolean {
    return false;
  }

  static getType(): string {
    return "image";
  }

  static clone(node: SlidImageNode): SlidImageNode {
    return new SlidImageNode(node.__data, node.getKey());
  }

  static importJSON(serializedNode: SerializedImageNode): SlidImageNode {
    return new SlidImageNode(serializedNode);
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("img");

    if (!this.__data.src) {
      element.setAttribute("src", store.getState().slidGlobal.lang === "ko" ? editorImageBlockErrorPlaceholderKo : editorImageBlockErrorPlaceholderEn);
    }

    const src = this.__data.src ? getCDNSrc(this.__data.src) : this.__data.src;
    if (src) element.setAttribute("src", src);
    if (this.__data.width) element.setAttribute("width", this.__data.width.toString());
    if (this.__data.height) element.setAttribute("height", this.__data.height.toString());
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: $convertImageElement,
        priority: 0,
      }),
    };
  }

  exportJSON(): SerializedImageNode {
    return {
      ...this.__data,
      type: "image",
      version: 1,
    } as SerializedImageNode;
  }

  setWidthAndHeight(width: "inherit" | number, height: "inherit" | number): void {
    const writable = this.getWritable();
    writable.__data.width = width;
    writable.__data.height = height;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement("div");
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      div.className = className;
    }
    return div;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    const src = this.__data.src ? getCDNSrc(this.__data.src) : this.__data.src;
    return src;
  }

  getData(): ImagePayload {
    return this.__data;
  }

  updateData(newData: Partial<ImagePayload>): void {
    const writableNode = this.getWritable();
    if (writableNode.__data) {
      Object.assign(writableNode.__data, newData);
    } else {
      Object.assign(writableNode, newData);
    }
  }

  decorate(): JSX.Element {
    const src = this.__data.src ? getCDNSrc(this.__data.src) : this.__data.src;

    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={src || (store.getState().slidGlobal.lang === "ko" ? editorImageBlockErrorPlaceholderKo : editorImageBlockErrorPlaceholderEn)}
          width={this.__data.width || "inherit"}
          height={this.__data.height || "inherit"}
          maxWidth={this.__data.maxWidth || 1000}
          nodeKey={this.getKey()}
          resizable={false}
          data={this.__data}
          isLoading={!this.__data.isUploaded}
          setClipData={this.__data.setClipData}
        />
      </Suspense>
    );
  }
}

export function $createImageNode(data: ImagePayload): SlidImageNode {
  return $applyNodeReplacement(new SlidImageNode(data));
}

export function $createErrorImageNode(): SlidImageNode {
  return $applyNodeReplacement(
    new SlidImageNode({
      src: store.getState().slidGlobal.lang === "ko" ? editorImageBlockErrorPlaceholderKo : editorImageBlockErrorPlaceholderEn,
      isUploaded: false,
      originalImageSrc: store.getState().slidGlobal.lang === "ko" ? editorImageBlockErrorPlaceholderKo : editorImageBlockErrorPlaceholderEn,
      sourceType: "fileUpload",
      documentKey: "",
    })
  );
}

export function $isImageNode(node: LexicalNode | null | undefined): node is SlidImageNode {
  return node instanceof SlidImageNode;
}
